/**
 * Sample report modal (onboarding tour, plan reveal/selection, empty states).
 *
 * A thin shell (banner, close, CTA) around the canonical report tree
 * (`components/report/ReportView`) rendering the sample fixture, which is a
 * persisted `analysis_structured` payload built by the real report code
 * (CORE-18, ONB-1/2/3/18/19). Browser Back closes it (ONB-23).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Modal, ScrollView, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { ReportBody, ReportHeader } from '@/components/report/ReportView';
import { getSampleReport } from '@/data/sampleReport';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import {
  type SampleReportSurface,
  trackSampleReportSectionViewed,
  trackSampleReportViewed,
} from '@/services/analytics';
import { normalizeLevelKey } from '@/utils/icfLevels';
import {
  ONBOARDING_COLORS,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_MODAL_MAX_HEIGHT,
  ONBOARDING_RADII,
  ONBOARDING_SPACING,
  ONBOARDING_TYPOGRAPHY,
} from './onboardingTheme';
import { useCloseOnBrowserBack } from './useCloseOnBrowserBack';
import { surface as surfaceTokens } from '../../design-system/theme/tokens';

const MODAL_MAX_WIDTH = 1120;

export interface SampleReportViewerProps {
  visible: boolean;
  onClose: () => void;
  surface: SampleReportSurface;
  primaryAction?: 'continue' | 'upload';
  onPrimaryPress?: () => void | Promise<void>;
  language?: string;
}

export default function SampleReportViewer({
  visible,
  onClose,
  surface,
  primaryAction = 'continue',
  onPrimaryPress,
  language,
}: SampleReportViewerProps) {
  const { t, language: currentLanguage } = useTranslation();
  const requestedLanguage = language ?? currentLanguage;
  const showLanguageNotice = requestedLanguage !== 'en' && requestedLanguage !== 'tr';
  const sample = useMemo(() => getSampleReport(requestedLanguage), [requestedLanguage]);
  const structured = sample.analysis_structured;
  const { isPhone } = useResponsive();
  const insets = useSafeAreaInsets();
  const primaryPendingRef = useRef(false);
  const [primaryPending, setPrimaryPending] = useState(false);

  useCloseOnBrowserBack(visible, onClose, 'sample_report');

  useEffect(() => {
    if (!visible) return;
    primaryPendingRef.current = false;
    setPrimaryPending(false);
    trackSampleReportViewed(surface, {
      sample_version: sample.meta.sample_version,
      section_expanded: false,
    });
  }, [sample.meta.sample_version, surface, visible]);

  const primaryLabel = t(primaryAction === 'upload' ? 'sampleReport.uploadCta' : 'sampleReport.continueCta');

  const handlePrimaryPress = async () => {
    if (primaryPendingRef.current) return;
    primaryPendingRef.current = true;
    setPrimaryPending(true);
    try {
      if (onPrimaryPress) {
        await onPrimaryPress();
      } else {
        onClose();
      }
    } finally {
      primaryPendingRef.current = false;
      setPrimaryPending(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType={isPhone ? 'slide' : 'fade'}
      transparent
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <SafeAreaView
        style={{
          flex: 1,
          minHeight: 0,
          backgroundColor: isPhone ? ONBOARDING_COLORS.background : ONBOARDING_COLORS.backdrop,
          justifyContent: 'center',
          alignItems: 'center',
          padding: isPhone ? 0 : ONBOARDING_SPACING.xl,
        }}
      >
        <View
          nativeID="sample_report_viewer"
          testID="sample_report_viewer"
          role="dialog"
          accessibilityViewIsModal
          accessibilityLabel={t('sampleReport.banner')}
          style={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            maxWidth: MODAL_MAX_WIDTH,
            maxHeight: isPhone ? '100%' : ONBOARDING_MODAL_MAX_HEIGHT,
            backgroundColor: ONBOARDING_COLORS.background,
            borderRadius: isPhone ? 0 : ONBOARDING_RADII.lg,
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              backgroundColor: ONBOARDING_COLORS.primary,
              paddingHorizontal: isPhone ? ONBOARDING_SPACING.lg : ONBOARDING_SPACING.xl,
              paddingVertical: ONBOARDING_SPACING.sm,
              flexDirection: 'row',
              alignItems: 'center',
              gap: ONBOARDING_SPACING.sm,
            }}
          >
            <MaterialIcons name="verified" size={ONBOARDING_SPACING.xl} color={ONBOARDING_COLORS.onPrimary} />
            <Text
              selectable
              style={{ flex: 1, color: ONBOARDING_COLORS.onPrimary, ...ONBOARDING_TYPOGRAPHY.caption, fontWeight: '700' }}
            >
              {t('sampleReport.banner')}
            </Text>
            <TouchableOpacity
              testID="sample_report_close"
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel={t('sampleReport.close')}
              style={{
                minWidth: ONBOARDING_CONTROL_HEIGHT,
                minHeight: ONBOARDING_CONTROL_HEIGHT,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name="close" size={ONBOARDING_SPACING.xl} color={ONBOARDING_COLORS.onPrimary} />
            </TouchableOpacity>
          </View>

          <ScrollView
            testID="sample_report_scroll"
            style={{ flex: 1, minHeight: 0, backgroundColor: surfaceTokens.canvas }}
            contentInsetAdjustmentBehavior="automatic"
            contentContainerStyle={{
              width: '100%',
              alignSelf: 'center',
              padding: isPhone ? ONBOARDING_SPACING.lg : ONBOARDING_SPACING.xl,
              // Clearance above the fixed CTA bar.
              paddingBottom: ONBOARDING_SPACING.xxl * 2,
              gap: ONBOARDING_SPACING.lg,
            }}
          >
            {showLanguageNotice ? (
              <View
                style={{
                  borderRadius: ONBOARDING_RADII.md,
                  backgroundColor: ONBOARDING_COLORS.primarySubtle,
                  padding: ONBOARDING_SPACING.md,
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: ONBOARDING_SPACING.sm,
                }}
              >
                <MaterialIcons name="language" size={ONBOARDING_SPACING.xl} color={ONBOARDING_COLORS.primary} />
                <Text
                  selectable
                  style={{ flex: 1, color: ONBOARDING_COLORS.text.secondary, ...ONBOARDING_TYPOGRAPHY.bodySmall }}
                >
                  {t('sampleReport.languageNotice')}
                </Text>
              </View>
            ) : null}
            <ReportHeader
              title={sample.meta.title}
              meta={[
                t('report.sample.meta', {
                  minutes: sample.meta.duration_minutes,
                  coach: sample.meta.coach_name,
                  client: sample.meta.client_name,
                }),
              ]}
              levelKey={normalizeLevelKey(structured.level_decision?.overall_level || structured.overall_level)}
              confidenceLabel={(structured.level_decision?.confidence_label as string) || null}
            />
            <ReportBody
              structured={structured}
              onCompetencyToggle={(code, open) => {
                if (open) trackSampleReportSectionViewed(`competency_${code.toLowerCase()}`);
              }}
            />
          </ScrollView>

          <View
            style={{
              paddingHorizontal: isPhone ? ONBOARDING_SPACING.lg : ONBOARDING_SPACING.xl,
              paddingTop: ONBOARDING_SPACING.sm,
              paddingBottom: Math.max(insets.bottom, ONBOARDING_SPACING.sm),
              borderTopWidth: 1,
              borderTopColor: ONBOARDING_COLORS.border,
              backgroundColor: ONBOARDING_COLORS.background,
            }}
          >
            <Button
              testID="sample_report_primary"
              variant="default"
              size="lg"
              onPress={handlePrimaryPress}
              disabled={primaryPending}
              accessibilityLabel={primaryLabel}
              accessibilityState={{ disabled: primaryPending, busy: primaryPending }}
              style={{
                minHeight: ONBOARDING_CONTROL_HEIGHT,
                backgroundColor: primaryPending ? ONBOARDING_COLORS.primaryDisabled : ONBOARDING_COLORS.primary,
              }}
            >
              {primaryPending ? <ActivityIndicator size="small" color={ONBOARDING_COLORS.onPrimary} /> : null}
              <Text style={{ color: ONBOARDING_COLORS.onPrimary, ...ONBOARDING_TYPOGRAPHY.button }}>
                {primaryLabel}
              </Text>
            </Button>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );
}
